import React, { useState, useRef, useEffect } from 'react';

export default function DDS70() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [results, setResults] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [modelInfo, setModelInfo] = useState(null);
  const [error, setError] = useState(null);
  const [isWebcamActive, setIsWebcamActive] = useState(false);
  const [webcamSupported, setWebcamSupported] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [isWebcamLoading, setIsWebcamLoading] = useState(false);
  const [mode, setMode] = useState('upload'); // 'upload' or 'webcam'
  const fileInputRef = useRef(null);
  const uploadAreaRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);

  // API base URL - Railway production or localhost for development
  const API_BASE_URL = process.env.REACT_APP_BASKETBALL_API_URL || 'https://doesdarrenshootlessthan70-production.up.railway.app';

  // Basketball-specific classes from the trained model
  const BASKETBALL_CLASSES = ["Basketball-courts", "ball", "made", "person", "rim", "shoot"];

  useEffect(() => {
    loadModelInfo();
    checkWebcamSupport();
    
    return () => {
      stopWebcam();
    };
  }, []);

  const checkWebcamSupport = async () => {
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        
        if (videoDevices.length > 0) {
          setWebcamSupported(true);
          setCameraError(null);
        } else {
          setWebcamSupported(false);
          setCameraError('No camera devices detected');
        }
      } else {
        setWebcamSupported(false);
        setCameraError('Camera access not supported by browser');
      }
    } catch (err) {
      setWebcamSupported(false);
      setCameraError('Unable to access camera devices');
    }
  };

  const startWebcam = async () => {
    try {
      setIsWebcamLoading(true);
      setCameraError(null);
      setError(null);
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { width: 640, height: 480 } 
      });
      
      setMode('webcam');
      
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          streamRef.current = stream;
          
          let webcamActivated = false;
          const activateWebcam = () => {
            if (webcamActivated) return;
            
            webcamActivated = true;
            setIsWebcamActive(true);
            setIsWebcamLoading(false);
            
            if (videoRef.current) {
              videoRef.current.style.display = 'block';
            }
            
            if (!intervalRef.current) {
              intervalRef.current = setInterval(() => {
                captureAndAnalyze();
              }, 3000);
              
              setTimeout(() => {
                captureAndAnalyze();
              }, 1000);
            }
          };

          videoRef.current.onloadedmetadata = () => activateWebcam();
          videoRef.current.onplaying = () => activateWebcam();
          videoRef.current.oncanplay = () => activateWebcam();
          
          setTimeout(() => {
            if (videoRef.current && videoRef.current.srcObject) {
              activateWebcam();
            }
          }, 3000);
          
          videoRef.current.play()
            .then(() => {
              console.log('Video playing successfully');
            })
            .catch(err => {
              console.warn('Video autoplay failed:', err);
              setTimeout(activateWebcam, 1000);
            });
        } else {
          setIsWebcamLoading(false);
          setCameraError('Failed to initialize video element');
        }
      }, 100);
    } catch (err) {
      setIsWebcamActive(false);
      setIsWebcamLoading(false);
      
      if (err.name === 'NotAllowedError') {
        setCameraError('Camera access denied. Please allow camera permissions and try again.');
      } else if (err.name === 'NotFoundError') {
        setCameraError('No camera found. Please connect a camera device.');
      } else if (err.name === 'NotSupportedError') {
        setCameraError('Camera not supported by this browser.');
      } else {
        setCameraError('Failed to access camera: ' + err.message);
      }
    }
  };

  const stopWebcam = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.load();
    }
    
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    
    setIsWebcamActive(false);
    setIsWebcamLoading(false);
    setResults(null);
    setCameraError(null);
  };

  const captureAndAnalyze = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const videoReady = videoRef.current.srcObject && videoRef.current.readyState >= 2;
    if (!videoReady) return;
    
    if (!isWebcamActive) {
      setIsWebcamActive(true);
      setIsWebcamLoading(false);
    }
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    if (video.readyState < 2 || video.videoWidth === 0 || video.videoHeight === 0) {
      return;
    }
    
    const rect = video.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
    
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    tempCanvas.width = video.videoWidth;
    tempCanvas.height = video.videoHeight;
    
    tempCtx.drawImage(video, 0, 0);
    
    tempCanvas.toBlob(async (blob) => {
      if (blob) {
        await analyzeWebcamFrame(blob);
      }
    }, 'image/jpeg', 0.8);
  };

  const drawDetectionBoxes = (detections, videoElement, overlayCanvas) => {
    if (!detections || !videoElement || !overlayCanvas) return;
    
    const ctx = overlayCanvas.getContext('2d');
    const videoRect = videoElement.getBoundingClientRect();
    const scaleX = videoRect.width / videoElement.videoWidth;
    const scaleY = videoRect.height / videoElement.videoHeight;
    
    ctx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
    
    detections.forEach((detection) => {
      const [x1, y1, x2, y2] = detection.bbox;
      const scaledX1 = x1 * scaleX;
      const scaledY1 = y1 * scaleY;
      const scaledX2 = x2 * scaleX;
      const scaledY2 = y2 * scaleY;
      const width = scaledX2 - scaledX1;
      const height = scaledY2 - scaledY1;
      
      const colors = {
        'ball': '#ff6b6b',
        'person': '#4ecdc4',
        'rim': '#45b7d1',
        'Basketball-courts': '#96ceb4',
        'made': '#feca57',
        'shoot': '#ff9ff3'
      };
      const color = colors[detection.class] || '#ffffff';
      
      ctx.strokeStyle = color;
      ctx.lineWidth = 3;
      ctx.strokeRect(scaledX1, scaledY1, width, height);
      
      const confidence = Math.ceil(detection.confidence * 100) / 100;
      const label = `${detection.class.trim()} ${confidence}`;
      ctx.font = 'bold 14px Arial';
      const textMetrics = ctx.measureText(label);
      const textWidth = textMetrics.width;
      const textHeight = 20;
      
      ctx.fillStyle = color;
      ctx.fillRect(scaledX1, scaledY1 - textHeight, textWidth + 8, textHeight);
      
      ctx.fillStyle = '#000000';
      ctx.fillText(label, scaledX1 + 4, scaledY1 - 6);
    });
  };

  const analyzeWebcamFrame = async (blob) => {
    try {
      const formData = new FormData();
      formData.append('image', blob, 'webcam-frame.jpg');

      const response = await fetch(`${API_BASE_URL}/api/detect?confidence=0.25`, {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.detections && Array.isArray(data.detections)) {
          const transformedResults = {
            detections: data.detections,
            processing_time: data.processing_time,
            total_objects: data.total_objects,
            model_used: data.model_used,
            image_size: data.image_size,
            from_api: true,
            realtime: true
          };
          setResults(transformedResults);
          drawDetectionBoxes(data.detections, videoRef.current, canvasRef.current);
          return;
        }
      }
    } catch (err) {
      console.log('Real-time detection temporarily unavailable');
    }
    
    setResults(null);
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  };

  const loadModelInfo = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/model-info`, {
        method: 'GET',
        timeout: 3000
      });
      
      if (response.ok) {
        const info = await response.json();
        setModelInfo(info);
      } else {
        throw new Error('Backend not available');
      }
    } catch (err) {
      setModelInfo({
        loaded: false,
        model_type: "YOLOv8 Custom Basketball Model",
        classes: BASKETBALL_CLASSES,
        performance: "Unavailable",
        dataset: "Basketball detection model",
        status: "Backend unavailable - please check connection"
      });
    }
  };

  const handleFileSelect = (file) => {
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setResults(null);
      setError(null);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    uploadAreaRef.current?.classList.add('dragover');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    uploadAreaRef.current?.classList.remove('dragover');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    uploadAreaRef.current?.classList.remove('dragover');
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const processImage = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('image', selectedFile);

      const response = await fetch(`${API_BASE_URL}/api/detect?confidence=0.25`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      
      if (response.ok && data.detections) {
        const transformedResults = {
          detections: data.detections,
          processing_time: data.processing_time,
          total_objects: data.total_objects,
          model_used: data.model_used,
          image_size: data.image_size,
          from_api: true,
          realtime: false
        };
        setResults(transformedResults);
      } else {
        setError(data.error || 'Detection failed - API error');
        setResults(null);
      }
    } catch (err) {
      setError('Failed to connect to detection API. Please check your connection.');
      setResults(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const clearResults = () => {
    setSelectedFile(null);
    setResults(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen transition-colors" style={{ backgroundColor: 'inherit' }}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="text-4xl font-bold mb-4" style={{ color: '#f6e05e' }}>
            DDS70 - Basketball Analytics System
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Advanced basketball detection using a custom YOLOv8 model trained on 10,000+ basketball images. 
            Detect basketball courts, players, balls, rims, and shooting actions with 74.1% mAP50-95 accuracy.
          </p>
        </div>



        {/* Error Display */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 text-destructive-foreground p-4 rounded-lg mb-6">
            <div className="flex items-center gap-2">
              <span>⚠️</span>
              <span className="font-medium">Error:</span>
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Mode Selection */}
        <div className="flex justify-center mb-6">
          <div className="bg-muted rounded-lg p-1 flex">
            <button
              onClick={() => {
                setMode('upload');
                stopWebcam();
                setCameraError(null);
              }}
              className={`px-4 py-2 rounded-md font-medium transition-all ${
                mode === 'upload' 
                  ? 'bg-primary text-primary-foreground shadow-sm' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              📁 Upload Image
            </button>
            <button
              onClick={() => {
                setMode('webcam');
                if (!webcamSupported) {
                  checkWebcamSupport();
                }
              }}
              disabled={false}
              className={`px-4 py-2 rounded-md font-medium transition-all ${
                mode === 'webcam' 
                  ? 'bg-primary text-primary-foreground shadow-sm' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              📹 Real-time Webcam
            </button>
          </div>
        </div>

        {/* Main Container */}
        <div className="bg-card border-border rounded-lg shadow-sm p-8 transition-colors duration-300">
          
          {mode === 'upload' ? (
            <>
              {/* Upload Section */}
              <div 
                ref={uploadAreaRef}
                className="upload-area border-3 border-dashed border-border rounded-lg p-8 text-center cursor-pointer transition-all duration-300 mb-6 bg-muted/30"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="upload-icon text-5xl mb-4" style={{ color: '#667eea' }}>
                  📷
                </div>
                <div className="upload-text text-lg text-muted-foreground mb-2">
                  {selectedFile ? selectedFile.name : 'Click to upload or drag and drop'}
                </div>
                <div className="upload-hint text-sm text-muted-foreground">
                  Supports JPG, PNG, WEBP files
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileInputChange}
                  className="hidden"
                />
              </div>

              {/* Upload Controls */}
              <div className="flex gap-4 mb-6 flex-wrap justify-center">
                <button
                  onClick={processImage}
                  disabled={!selectedFile || isProcessing}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium transition-all hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                      Analyzing...
                    </>
                  ) : (
                    'Analyze Image'
                  )}
                </button>
                <button
                  onClick={clearResults}
                  className="px-6 py-3 bg-secondary text-secondary-foreground rounded-lg font-medium transition-all hover:bg-secondary/90"
                >
                  Clear Results
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Webcam Section */}
              <div className="text-center mb-6">
                <div className="bg-muted rounded-lg p-6 mb-4 max-w-2xl mx-auto">
                  {cameraError ? (
                    <div>
                      <div className="text-4xl mb-2">❌</div>
                      <div className="text-lg font-medium text-card-foreground mb-2">Camera Not Available</div>
                      <div className="text-sm text-destructive mb-4">
                        {cameraError}
                      </div>
                      <button
                        onClick={checkWebcamSupport}
                        className="px-6 py-3 bg-secondary text-secondary-foreground rounded-lg font-medium transition-all hover:bg-secondary/90"
                      >
                        Retry Camera Detection
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div className="relative inline-block mb-4">
                        <video 
                          ref={videoRef}
                          autoPlay 
                          playsInline 
                          muted
                          className="w-full max-w-lg rounded-lg border border-gray-300"
                          style={{ 
                            width: '100%',
                            maxWidth: '512px',
                            height: 'auto',
                            backgroundColor: '#000000',
                            display: 'block'
                          }}
                        />
                        <canvas 
                          ref={canvasRef} 
                          className="absolute top-0 left-0 rounded-lg pointer-events-none"
                          style={{ 
                            width: '100%',
                            height: '100%',
                            maxWidth: '512px',
                            maxHeight: 'auto',
                            display: 'block'
                          }}
                        />
                        
                        {/* Show controls overlay when webcam is not active and video not ready */}
                        {(!isWebcamActive && !isWebcamLoading && (!videoRef.current?.srcObject || videoRef.current?.readyState < 2)) && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 bg-opacity-90 rounded-lg">
                            <div className="text-4xl mb-2">📹</div>
                            <div className="text-lg font-medium text-gray-800 mb-2">Real-time Basketball Detection</div>
                            <div className="text-sm text-gray-600 mb-4 text-center px-4">
                              {webcamSupported 
                                ? "Click \"Start Webcam\" to begin live detection analysis"
                                : "Checking camera availability..."
                              }
                            </div>
                            <button
                              onClick={startWebcam}
                              disabled={!webcamSupported}
                              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                                webcamSupported
                                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                  : 'bg-gray-400 text-gray-700 cursor-not-allowed'
                              }`}
                            >
                              Start Webcam
                            </button>
                          </div>
                        )}
                        
                        {/* Show loading state */}
                        {isWebcamLoading && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 bg-opacity-90 rounded-lg">
                            <div className="text-4xl mb-2">⏳</div>
                            <div className="text-lg font-medium text-gray-800 mb-2">Starting Camera...</div>
                            <div className="flex items-center">
                              <span className="inline-block w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></span>
                              Connecting to webcam...
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Show stop button when webcam is active or video is ready */}
                      {(isWebcamActive || (videoRef.current?.srcObject && videoRef.current?.readyState >= 2)) && (
                        <div className="flex gap-3 justify-center">
                          <button
                            onClick={stopWebcam}
                            className="px-6 py-3 bg-destructive text-destructive-foreground rounded-lg font-medium transition-all hover:bg-destructive/90"
                          >
                            Stop Webcam
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Image Preview - Only show in upload mode */}
          {selectedFile && mode === 'upload' && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 text-card-foreground">Selected Image:</h3>
              <div className="bg-muted rounded-lg p-4 text-center">
                <img
                  src={URL.createObjectURL(selectedFile)}
                  alt="Selected"
                  className="max-w-full max-h-96 mx-auto rounded-lg shadow-sm"
                />
              </div>
            </div>
          )}

          {/* Results */}
          {results && (
            <div className="results-section">
              <h3 className="text-lg font-semibold mb-4 text-card-foreground">Detection Results:</h3>
              
              {/* Improved visibility for real-time detection status */}
              <div className={`p-3 rounded-lg mb-4 text-sm font-medium ${
                results.realtime 
                  ? 'bg-green-100 border-2 border-green-400 text-green-800' 
                  : 'bg-blue-100 border-2 border-blue-400 text-blue-800'
              }`}>
                <div className="flex items-center gap-2">
                  <span className="text-lg">{results.realtime ? '🎥' : '📸'}</span>
                  <span>
                    {results.realtime 
                      ? 'Live Real-time Detection Active' 
                      : 'Static Image Analysis Complete'}
                  </span>
                  <span className="text-xs opacity-75">
                    (YOLOv8 Model)
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-muted rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-primary">{results.total_objects}</div>
                  <div className="text-sm text-muted-foreground">Objects Detected</div>
                </div>
                <div className="bg-muted rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-primary">{results.processing_time}</div>
                  <div className="text-sm text-muted-foreground">Processing Time</div>
                </div>
                <div className="bg-muted rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-primary">YOLOv8</div>
                  <div className="text-sm text-muted-foreground">Model Used</div>
                </div>
              </div>

              <div className="detections">
                <h4 className="text-md font-semibold mb-3 text-card-foreground">Detected Objects:</h4>
                <div className="space-y-2">
                  {results.detections.map((detection, index) => (
                    <div key={index} className="bg-muted rounded-lg p-3 flex justify-between items-center">
                      <div>
                        <span className="font-medium text-card-foreground capitalize">{detection.class}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm bg-primary text-primary-foreground px-2 py-1 rounded">
                          {(detection.confidence * 100).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Technical Details */}
          <div className="mt-8 pt-6 border-t border-border">
            <h4 className="text-md font-semibold mb-3 text-card-foreground">Technical Details:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
              <div>
                <strong>Model:</strong> YOLOv8 (You Only Look Once v8)
              </div>
              <div>
                <strong>Framework:</strong> Ultralytics PyTorch
              </div>
              <div>
                <strong>Dataset:</strong> Custom 10K+ basketball images
              </div>
              <div>
                <strong>Performance:</strong> 74.1% mAP50-95 accuracy
              </div>
            </div>
          </div>

          {/* Links */}
          <div className="mt-6 pt-4 border-t border-border flex gap-4 justify-center">
            <a
              href="https://github.com/pyoos/DoesDarrenShootLessThan70-"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary/80 font-medium text-sm transition-colors"
            >
              View Source Code →
            </a>
            <a
              href="/projects"
              className="text-muted-foreground hover:text-foreground font-medium text-sm transition-colors"
            >
              ← Back to Projects
            </a>
          </div>
        </div>
      </div>
    </div>
  );
} 